/**
 * Polar.sh cross-service contracts.
 *
 * Lives here — next to `signatures.ts`, `callback.ts`, `schemas.ts` —
 * because the Polar webhook envelope + signature verifier is
 * consumed by both `apps/webhooks` (inbound verification) and
 * `apps/api` (PolarClient parses REST responses through the same
 * schemas). One contract, two consumers — exactly the pattern this
 * package exists to solve.
 *
 * Every schema uses `.passthrough()` because Polar ships fast. We care
 * about the fields we enumerate; we deliberately do not reject
 * unknown fields.
 *
 * Spec references (from <https://polar.sh/docs/llms-full.txt>):
 * - Products / prices: `/v1/products/`, `/v1/meters/`
 * - Subscriptions: `/v1/subscriptions/{id}` (immediate-proration
 *   updates — Phase 4)
 * - Customers: `/v1/customers/*`, `/v1/customers/external/{externalId}`
 * - Checkouts: `/v1/checkouts/`
 * - Events: `/v1/events/ingest`
 * - Webhook envelope: Standard Webhooks spec
 *   (https://www.standardwebhooks.com/) — headers `webhook-id`,
 *   `webhook-timestamp`, `webhook-signature`.
 */
import { z } from 'zod';

// ─── Plan helpers (pure, no IO) ──────────────────────────────────────────────

export const POLAR_PLAN_SLUGS = ['indie', 'startup', 'pro', 'business'] as const;
export type PolarPlanSlug = (typeof POLAR_PLAN_SLUGS)[number];

/** Rank used by plan-change / upsell logic. `indie` is the cheapest. */
const PLAN_RANK = {
  indie: 0,
  startup: 1,
  pro: 2,
  business: 3,
} as const satisfies Record<PolarPlanSlug, 0 | 1 | 2 | 3>;

export function planRank(plan: PolarPlanSlug): 0 | 1 | 2 | 3 {
  return PLAN_RANK[plan];
}

/**
 * Polar product ids keyed by plan slug. The map is passed in by
 * callers (sourced from env) — the shared module never hardcodes
 * sandbox ids, so live-mode flips are a config swap.
 */
export interface PolarProductMap {
  indie: string;
  startup: string;
  pro: string;
  business: string;
}

export function productIdForPlan(plan: PolarPlanSlug, map: PolarProductMap): string {
  return map[plan];
}

export function planForProductId(productId: string, map: PolarProductMap): PolarPlanSlug | null {
  for (const slug of POLAR_PLAN_SLUGS) {
    if (map[slug] === productId) return slug;
  }
  return null;
}

/**
 * Parse a Polar product's `metadata.plan_slug` into a known slug.
 * Returns null for unknown / missing values so callers can gate on
 * `if (!slug) return 204;` without throwing.
 *
 * This is the preferred resolver at webhook time: product ids can
 * change (re-creation, sandbox→live), but the `metadata.plan_slug`
 * contract is set by us and is stable.
 */
export function planFromProductMetadata(
  metadata: Record<string, unknown> | null | undefined,
): PolarPlanSlug | null {
  if (!metadata) return null;
  const raw = metadata.plan_slug;
  if (typeof raw !== 'string') return null;
  return (POLAR_PLAN_SLUGS as readonly string[]).includes(raw) ? (raw as PolarPlanSlug) : null;
}

// ─── Common field schemas ────────────────────────────────────────────────────

const uuid = z.string().uuid();
const isoTimestamp = z.string().datetime({ offset: true });

/**
 * Polar metadata shape (spec: string|int|float|bool values,
 * ≤50 keys, keys ≤40 chars, string values ≤500 chars). We model it
 * permissively — the API enforces shape server-side.
 */
const metadata = z
  .record(z.union([z.string(), z.number(), z.boolean()]))
  .optional()
  .nullable();

// ─── Customer ────────────────────────────────────────────────────────────────

export const polarCustomerSchema = z
  .object({
    id: uuid,
    external_id: z.string().nullable().optional(),
    email: z.string().email().nullable().optional(),
    email_verified: z.boolean().optional(),
    name: z.string().nullable().optional(),
    organization_id: uuid,
    metadata: metadata,
    created_at: isoTimestamp,
    modified_at: isoTimestamp.nullable().optional(),
    deleted_at: isoTimestamp.nullable().optional(),
  })
  .passthrough();
export type PolarCustomer = z.infer<typeof polarCustomerSchema>;

// ─── Product / price (minimum we touch) ──────────────────────────────────────

const polarPriceSchema = z
  .object({
    id: uuid,
    amount_type: z.enum(['fixed', 'metered_unit', 'custom', 'free', 'seat_based']),
    price_currency: z.string().optional(),
    price_amount: z.number().int().optional(),
    unit_amount: z.union([z.string(), z.number()]).optional(),
    meter_id: uuid.optional(),
    product_id: uuid,
    type: z.enum(['recurring', 'one_time']).optional(),
    recurring_interval: z.enum(['day', 'week', 'month', 'year']).nullable().optional(),
    is_archived: z.boolean().optional(),
  })
  .passthrough();
export type PolarPrice = z.infer<typeof polarPriceSchema>;

export const polarProductSchema = z
  .object({
    id: uuid,
    name: z.string(),
    description: z.string().nullable().optional(),
    organization_id: uuid,
    is_archived: z.boolean().optional(),
    is_recurring: z.boolean().optional(),
    recurring_interval: z.enum(['day', 'week', 'month', 'year']).nullable().optional(),
    metadata: metadata,
    prices: z.array(polarPriceSchema).default([]),
    created_at: isoTimestamp,
    modified_at: isoTimestamp.nullable().optional(),
  })
  .passthrough();
export type PolarProduct = z.infer<typeof polarProductSchema>;

// ─── Subscription ────────────────────────────────────────────────────────────

/**
 * Polar subscription statuses we care about. Polar's enum is slightly
 * larger (`incomplete`, `incomplete_expired`, `trialing`, `active`,
 * `past_due`, `canceled`, `unpaid`); any unknown string passes through
 * thanks to `.catch()`, and the webhook handler branches on what it
 * recognises.
 */
export const POLAR_SUBSCRIPTION_STATUSES = [
  'incomplete',
  'incomplete_expired',
  'trialing',
  'active',
  'past_due',
  'canceled',
  'unpaid',
] as const;
export type PolarSubscriptionStatus = (typeof POLAR_SUBSCRIPTION_STATUSES)[number];

/** Statuses that mean the subscription no longer grants entitlement. */
export const TERMINAL_POLAR_STATUSES: ReadonlySet<PolarSubscriptionStatus> = new Set([
  'incomplete_expired',
  'canceled',
  'unpaid',
]);

export const polarSubscriptionSchema = z
  .object({
    id: uuid,
    status: z.string(),
    customer_id: uuid,
    product_id: uuid,
    // Polar's REST response for `GET /v1/subscriptions/{id}` does NOT
    // carry a top-level `organization_id` on the subscription object —
    // the org id lives on the nested `product.organization_id` instead.
    // The field is kept optional here for defensive forward-compat in
    // case Polar ever starts inlining it (as their `product`/`customer`
    // schemas already do). Requiring it was a Phase-3 spec bug: the
    // handler 503'd on every real subscription event.
    organization_id: uuid.optional(),
    current_period_start: isoTimestamp.nullable().optional(),
    current_period_end: isoTimestamp.nullable().optional(),
    cancel_at_period_end: z.boolean().optional(),
    canceled_at: isoTimestamp.nullable().optional(),
    started_at: isoTimestamp.nullable().optional(),
    ends_at: isoTimestamp.nullable().optional(),
    ended_at: isoTimestamp.nullable().optional(),
    metadata: metadata,
    created_at: isoTimestamp,
    modified_at: isoTimestamp.nullable().optional(),
    // Polar sometimes inlines a `product` object on events; we capture it
    // so the webhook handler can skip a follow-up GET when it's there.
    product: polarProductSchema.optional(),
  })
  .passthrough();
export type PolarSubscription = z.infer<typeof polarSubscriptionSchema>;

// ─── Checkout session ────────────────────────────────────────────────────────

export const polarCheckoutSchema = z
  .object({
    id: uuid,
    url: z.string().url(),
    client_secret: z.string().optional(),
    customer_id: uuid.nullable().optional(),
    customer_email: z.string().email().nullable().optional(),
    customer_external_id: z.string().nullable().optional(),
    product_id: uuid.optional(),
    products: z.array(polarProductSchema).optional(),
    success_url: z.string().url().nullable().optional(),
    status: z.string().optional(),
    metadata: metadata,
    expires_at: isoTimestamp.nullable().optional(),
    created_at: isoTimestamp,
  })
  .passthrough();
export type PolarCheckout = z.infer<typeof polarCheckoutSchema>;

// ─── Order ───────────────────────────────────────────────────────────────────

export const polarOrderSchema = z
  .object({
    id: uuid,
    status: z.string(),
    customer_id: uuid,
    subscription_id: uuid.nullable().optional(),
    product_id: uuid.optional(),
    organization_id: uuid,
    amount: z.number().int().optional(),
    tax_amount: z.number().int().optional(),
    currency: z.string().optional(),
    created_at: isoTimestamp,
    modified_at: isoTimestamp.nullable().optional(),
    metadata: metadata,
  })
  .passthrough();
export type PolarOrder = z.infer<typeof polarOrderSchema>;

// ─── Meter event (usage ingestion response) ──────────────────────────────────

export const polarMeterEventSchema = z
  .object({
    id: uuid,
    name: z.string(),
    external_customer_id: z.string().nullable().optional(),
    customer_id: uuid.nullable().optional(),
    organization_id: uuid,
    metadata: metadata,
    timestamp: isoTimestamp,
  })
  .passthrough();
export type PolarMeterEvent = z.infer<typeof polarMeterEventSchema>;

/**
 * Response shape of `POST /v1/events/ingest` — `{ inserted: number }`.
 * The top-level body wraps a batch; we always send 1 event at a time
 * (Phase 5 queue consumer batches at the queue layer, not the HTTP
 * layer) but we still parse the count so the caller can assert `== 1`.
 */
export const polarIngestResponseSchema = z
  .object({
    inserted: z.number().int().nonnegative(),
  })
  .passthrough();
export type PolarIngestResponse = z.infer<typeof polarIngestResponseSchema>;

// ─── Webhook envelope ────────────────────────────────────────────────────────

/**
 * Polar webhook envelope. `type` discriminates the handler branch;
 * `data` is parsed through the appropriate payload schema
 * (`polarSubscriptionSchema`, `polarOrderSchema`, etc.) downstream.
 *
 * The top-level `id` field is the event id (separate from
 * `webhook-id` header — which standardwebhooks uses for signature
 * verification). Events are immutable; re-deliveries carry the same
 * event `id` and are safe to dedupe on.
 */
export const polarWebhookEnvelopeSchema = z
  .object({
    type: z.string(),
    data: z.unknown(),
  })
  .passthrough();
export type PolarWebhookEnvelope = z.infer<typeof polarWebhookEnvelopeSchema>;
